import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../Context/AuthContext'
import Loader from '../../Common/Loader'
import logo from '../assets/logo.svg'
import './Signup.css'

function Signup() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [displayedText, setDisplayedText] = useState('')
  const [showCursor, setShowCursor] = useState(true)
  
  const navigate = useNavigate()
  const { signUp, signInWithOAuth, user, session, loading, isAuthenticated } = useAuth()
  
  // Subtitle text for typing animation
  const subtitleText = "Your One-Stop Business Solution"

  // Typing animation effect
  useEffect(() => {
    let currentIndex = 0
    const typingInterval = setInterval(() => {
      if (currentIndex <= subtitleText.length) {
        setDisplayedText(subtitleText.slice(0, currentIndex))
        currentIndex++
      } else {
        clearInterval(typingInterval)
        setShowCursor(true)
      }
    }, 50)

    return () => clearInterval(typingInterval)
  }, [])

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, loading, navigate])

  // Countdown effect for successful signups
  useEffect(() => {
    let timer
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000)
    } else if (countdown === 0 && messageType === 'success') {
      navigate('/dashboard')
    }
    return () => clearTimeout(timer)
  }, [countdown, messageType, navigate])

  const handleGoogleSignup = async () => {
    setIsGoogleLoading(true)
    setMessage('')
    
    try {
      const { error } = await signInWithOAuth('google', `${window.location.origin}/dashboard`)
      
      if (error) {
        setMessage(error.message)
        setMessageType('error')
      }
    } catch (err) {
      console.error('Google signup error:', err)
      setMessage('An error occurred during Google signup')
      setMessageType('error')
    } finally {
      setIsGoogleLoading(false)
    }
  }

  const handleSignup = async (e) => {
    e.preventDefault()
    
    if (!email || !password) {
      setMessage('Please fill in all fields.')
      setMessageType('error')
      return
    }

    if (password.length < 6) {
      setMessage('Password must be at least 6 characters long.')
      setMessageType('error')
      return
    }

    setIsLoading(true)
    setMessage('')
    
    try {
      const { data, error } = await signUp(email, password, {
        emailRedirectTo: `${window.location.origin}/dashboard`
      })
      
      if (error) {
        setMessage(error.message)
        setMessageType('error')
      } else if (data.user) {
        if (data.user.email_confirmed_at) {
          setMessage('Account created successfully!')
          setMessageType('success')
          setCountdown(3)
        } else {
          setMessage('Please check your email and click the confirmation link to complete your registration.')
          setMessageType('info')
          setEmail('')
          setPassword('')
        }
      }
    } catch (err) {
      console.error('Signup error:', err)
      setMessage('An unexpected error occurred. Please try again.')
      setMessageType('error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSignup(e)
    }
  }

  // Show loading if auth is still initializing
  if (loading) {
    return (
      <div className="Signup-container">
        <div className="Signup-card">
          <Loader />
        </div>
      </div>
    )
  }

  return (
    <div className="Signup-container">
      <div className="Signup-wrapper">
        {/* Left Section - Image and Subtitle */}
        <div className="Signup-left-section">
          <div className="Signup-image-container">
            <img 
              src={logo} 
              alt="Logo" 
              className="Signup-image"
            />
          </div>
          
          <div className="Signup-subtitle-container">
            <p className="Signup-subtitle">
              {displayedText}
              {showCursor && <span className="Signup-subtitle-cursor"></span>}
            </p>
          </div>
        </div>

        {/* Right Section - Signup Form */}
        <div className="Signup-right-section">
          <div className="Signup-card">
            {(isLoading || isGoogleLoading) && (
              <div className="Signup-loading-overlay">
                <Loader />
              </div>
            )}
            
            <h2 className="Signup-title">Create Account</h2>
            
            <form className="Signup-form" onSubmit={handleSignup}>
              <input 
                className="Signup-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Email Address"
                required
                disabled={isLoading || isGoogleLoading}
              />
              
              <input 
                className="Signup-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Password (min. 6 characters)"
                required
                disabled={isLoading || isGoogleLoading}
                minLength={6}
              />
              
              <button 
                className="Signup-button"
                type="submit"
                disabled={isLoading || isGoogleLoading || !email || !password}
              >
                {isLoading ? <Loader /> : 'Create Account'}
              </button>
            </form>

            <hr className="Signup-divider" />
            
            <button 
              className="Signup-google-button"
              onClick={handleGoogleSignup}
              disabled={isLoading || isGoogleLoading}
            >
              {isGoogleLoading ? <Loader /> : 'Continue with Google'}
            </button>

            {message && (
              <div className={`Signup-message Signup-message--${messageType}`}>
                {message}
                {countdown > 0 && messageType === 'success' && (
                  <span>
                    {' '}Redirecting in <span className="Signup-countdown">{countdown}</span> seconds...
                  </span>
                )}
              </div>
            )}

            <div className="Signup-links">
              <p className="Signup-link-text">
                Already have an account? <Link to="/login" className="Signup-link">Sign In</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Signup